import React, { useMemo } from 'react'

import { Paragraph } from '../../components'
import { ChartContext } from '../../components/chart/ChartWrapper'
import { useFinancialsTableContext } from '../../components/financials-table'
import { WidgetHeader } from '../earnings-common/WidgetHeader'
import { EarningsEstimateType, EarningsSettings } from '../earnings-common/types'
import { FiscalPeriodSelector } from './FiscalPeriodSelector'
import { PriceRevisionsChart } from './charts/PriceRevisionsChart'
import { UpDownRevisionsChart } from './charts/UpDownRevisionsChart'
import { FISCAL_YEAR_MARK } from './constants'
import { EarningsRevisionsByType, RevisionsChartData } from './types'
import { adjustEstimate } from './utils'

function getDefaultFiscalPeriod(revisionData: EarningsRevisionsByType, estimateType: EarningsEstimateType) {
  const years = Object.keys(revisionData[estimateType].years)
  if (years.length > 0) {
    return years[years.length - 1]
  }
  const quarters = Object.keys(revisionData[estimateType].quarters)
  return quarters[quarters.length - 1]
}

interface Props {
  revisionData: EarningsRevisionsByType
}

export default function EstimateRevisions({ revisionData }: Props) {
  const { settings, setSettings } = useFinancialsTableContext<EarningsSettings>()

  const estimateType = settings.estimateType
  const prepFiscalPeriod = settings.fiscalPeriod
    ? settings.fiscalPeriod
    : getDefaultFiscalPeriod(revisionData, estimateType)

  const data = useMemo(() => {
    const chartData: RevisionsChartData = []
    const years = Object.keys(revisionData[estimateType].years)
    const quarters = Object.keys(revisionData[estimateType].quarters)
    const fiscalPeriods = [...years, ...quarters]
    let fiscalPeriod = prepFiscalPeriod
    if (fiscalPeriod && !fiscalPeriods.includes(fiscalPeriod)) {
      fiscalPeriod = fiscalPeriod.endsWith(FISCAL_YEAR_MARK) ? years[years.length - 1] : quarters[quarters.length - 1]
    }
    if (!fiscalPeriod) {
      fiscalPeriod = years.length > 0 ? years[years.length - 1] : quarters[quarters.length - 1]
    }

    if (fiscalPeriod) {
      const revisions = fiscalPeriod.endsWith(FISCAL_YEAR_MARK)
        ? revisionData[estimateType].years[fiscalPeriod]
        : revisionData[estimateType].quarters[fiscalPeriod]
      for (const revision of revisions) {
        chartData.push({
          ...revision,
          high: adjustEstimate(revision.high, estimateType),
          low: adjustEstimate(revision.low, estimateType),
          lowHigh: [adjustEstimate(revision.low, estimateType), adjustEstimate(revision.high, estimateType)],
          mean: adjustEstimate(revision.mean, estimateType),
          negDownRevisions: -revision.downRevisions,
          timestamp: revision.date.getTime(),
        })
      }
    }

    const startingTimestamp = chartData.length ? chartData[0].timestamp : 0
    const endingTimestamp = chartData.length ? chartData[chartData.length - 1].timestamp : 0

    return {
      chartData,
      endingTimestamp,
      fiscalPeriod,
      fiscalPeriods,
      startingTimestamp,
    }
  }, [estimateType, prepFiscalPeriod, revisionData])

  if (data.chartData.length === 0) {
    return null
  }

  return (
    <div className="mt-3 border-t border-primary pt-3">
      <div className="flex items-center pb-3">
        <WidgetHeader>Estimate Revisions</WidgetHeader>
        <div className="flex-grow"></div>
        <Paragraph size="small" className="ml-5 pr-1 text-muted-2 font-medium">
          Period
        </Paragraph>
        <div className="ml-2 inline-block w-22">
          <FiscalPeriodSelector
            selected={data.fiscalPeriod}
            fiscalPeriods={data.fiscalPeriods}
            onSelect={(selected) => setSettings({ ...settings, fiscalPeriod: selected })}
          />
        </div>
      </div>
      <ChartContext isHoverHighlight={false}>
        <PriceRevisionsChart
          chartData={data.chartData}
          endingTimestamp={data.endingTimestamp}
          estimateType={estimateType}
          startingTimestamp={data.startingTimestamp}
        />
        <UpDownRevisionsChart
          chartData={data.chartData}
          endingTimestamp={data.endingTimestamp}
          startingTimestamp={data.startingTimestamp}
        />
      </ChartContext>
    </div>
  )
}
